if (!customElements.get('products-toolbar')) {
  // Caps concurrent Section Rendering requests when fetching every other page for a global
  // SKU search — matches the number of same-origin connections most browsers keep open per
  // host, so this is roughly the most that could ever actually be in flight at once.
  const SKU_FETCH_CONCURRENCY = 6;

  // Copied from snippets/icon-chevron-left.liquid, icon-chevron-right.liquid, and the
  // inline first/last-page SVGs in snippets/nu--pagination.liquid — the client-rendered
  // pagination fallback (no spec-filters container, or an active SKU search) can't call
  // {% render %}, so these need to be kept in sync with those snippets by hand.
  const PAGINATION_PREV_ICON_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z"/></svg>';
  const PAGINATION_NEXT_ICON_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';
  const PAGINATION_FIRST_ICON_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" class="icon icon--pagination-first"><path d="M4.5 4.5v15" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/><path d="m6.797 11.625 8.03-8.03 1.06 1.06-6.97 6.97 6.97 6.97-1.06 1.06z" fill="currentColor"/></svg>';
  const PAGINATION_LAST_ICON_SVG = '<svg width="24" height="24" viewBox="0 0 24 24" focusable="false" role="presentation" class="icon icon--pagination-last"><path d="m9.693 4.5 7.5 7.5-7.5 7.5" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M19.5 4.5v15" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>';

  class ProductsToolbar extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      this.filtersLite = window.NuFiltersLite || null;
      this.filtersColumn = document.querySelector('.main-products-grid__filters');
      this.mobileFiltersOpenBtn = document.querySelector('[data-nu-mobile-filters-open]');
      this.mobileFiltersCloseBtn = document.querySelector('[data-nu-mobile-filters-close]');
      this.layoutSwitcher = this.querySelector('.products-toolbar__layout');
      this.sortBy = this.querySelector('.products-toolbar__sort');
      this.productsGrid = document.querySelector('.main-products-grid');
      this.productsTotal = document.querySelector('[data-nu-products-total]');
      // Labels for the client-rendered pagination fallback (renderRowsClientPagination) —
      // read once here so that function doesn't need a container reference of its own.
      this.paginationLabel = this.dataset.paginationLabel || 'Pagination';
      this.paginationPreviousLabel = this.dataset.paginationPrevious || 'Previous';
      this.paginationNextLabel = this.dataset.paginationNext || 'Next';
      this.paginationPositionTemplate = this.dataset.paginationPositionTemplate || 'Page [current] / [total]';
      this.paginationFirstPageLabel = this.dataset.paginationFirstPageLabel || 'First page';
      this.paginationLastPageLabel = this.dataset.paginationLastPageLabel || 'Last page';
      this.skuSearchForm = this.querySelector('[data-nu-sku-search-form]');
      this.skuSearchInput = this.querySelector('[data-nu-sku-search]');
      this.skuSearchClearBtn = this.querySelector('[data-nu-sku-search-clear]');
      this.skuSearchSubmitBtn = this.querySelector('[data-nu-sku-search-submit]');
      // Lives in the table markup (sections/nu--main-collection-products.liquid), a sibling of
      // this custom element, not a descendant — document.querySelector, not this.querySelector.
      this.skuSearchStatusEl = document.querySelector('[data-nu-sku-search-status]');
      // Captured once so setRowsLoadingStatusVisible() (H-34/H-37) can restore the SKU-search
      // wording after borrowing this same element/overlay for its own "Loading…" text.
      this.skuSearchStatusDefaultText = this.skuSearchStatusEl?.textContent || '';
      const skuQueryFromUrl = this.getSkuQueryFromUrl();
      if (this.skuSearchInput && skuQueryFromUrl && !this.skuSearchInput.value.trim()) {
        this.skuSearchInput.value = skuQueryFromUrl;
      }
      this.skuSearchQuery = this.getSkuSearchInputQuery();
      this.r4qRefreshTimer = null;
      this.r4qRefreshDelayMs = 160;
      this.r4qBusyClearTimer = null;
      this.r4qBusyVersion = 0;
      this.r4qBusyShowDelayMs = 150;
      this.r4qBusyMinVisibleMs = 280;
      this.r4qBusyVisibleSince = 0;
      this.r4qBusyShowTimer = null;
      this.rowsChangeInFlight = false;
      this.rowsTruncationToken = 0;
      this.baseResultsCount = Number(this.productsTotal?.getAttribute('data-base-count') || '0');
      this.skuGlobalCache = null;
      this.skuGlobalFetchPromise = null;
      this.skuGlobalFetchController = null;
      this.skuSearchRequestToken = 0;
      this.skuServerSnapshot = {
        rowsHtml: '',
        paginationHtml: '',
        page: 1,
      };
      // The SSR page exactly as the server rendered it, taken before any client-side
      // truncation/pagination touches the DOM (see getGlobalRenderedProductsForSku()).
      this.ssrRenderedProducts = ProductsToolbar.extractRenderedProducts(document);
      this.ssrTotalPages = this.getTotalPagesFromPagination();
      this.ssrPage = this.getCurrentPageFromUrl();
      this.rowsSelect = document.querySelector('[data-nu-rows-select]');
      this.onRowsPerPageChange = this.handleRowsPerPageChange.bind(this);
      this.tableScroller = document.querySelector('.nu-products-table');
      this.tableElement = this.tableScroller?.querySelector('.nu-products-table__table') || null;
      this.tableHead = this.tableElement?.querySelector('.nu-products-table__head') || null;
      this.layoutRoot = document.querySelector('.nu--collection-layout');
      this.stickyHeaderEl = document.querySelector('.cc-header');
      this.onProductsCountUpdate = this.handleProductsCountUpdate.bind(this);
      this.onTableScroll = this.handleTableScroll.bind(this);
      this.onTableScrollerPointerDown = this.handleTableScrollerPointerDown.bind(this);
      this.onTableScrollerPointerMove = this.handleTableScrollerPointerMove.bind(this);
      this.onTableScrollerPointerUp = this.handleTableScrollerPointerUp.bind(this);
      this.onVirtualScrollbarScroll = this.handleVirtualScrollbarScroll.bind(this);
      this.tableDragScroll = { active: false, moved: false, startX: 0, startScrollLeft: 0 };
      this.isSyncingVirtualScrollbar = false;
      this.onWindowScroll = this.handleFloatingHeadPosition.bind(this);
      this.onWindowResize = this.handleFloatingHeadPosition.bind(this);
      this.onWindowKeydown = this.handleWindowKeydown.bind(this);
      this.onNuSpecFiltersReady = this.handleNuSpecFiltersReady.bind(this);
      this.onR4QRefreshDone = this.handleR4QRefreshDone.bind(this);
      this.onR4QRefreshRequested = this.handleR4QRefreshRequested.bind(this);
      this.onR4QBusyTriggerClick = this.handleR4QBusyTriggerClick.bind(this);
      this.floatingHeadRafId = null;
      this.floatingHeadRafPending = false;
      this.r4qBusyNextRefresh = false;
      document.addEventListener('nu:products-count:update', this.onProductsCountUpdate);
      document.addEventListener('nu:spec-filters:ready', this.onNuSpecFiltersReady);
      document.addEventListener('nu:r4q:refresh', this.onR4QRefreshRequested);
      document.addEventListener('nu:r4q:refresh:done', this.onR4QRefreshDone);
      document.addEventListener('click', this.onR4QBusyTriggerClick, true);

      // Must run after the nu_rows truncation below (reads the already-corrected row count)
      // and, just as importantly, before handleProductsCountUpdate() below — with no
      // spec-filters container and a ?nu_sku=... already on the URL on cold load, that call
      // synchronously applies the global SKU filter (applyGlobalSkuSearchFilter()'s fast path
      // runs before its first await), which rewrites the grid to a single filtered page. If
      // the snapshot were taken after that, "clear search" would restore this filtered page
      // instead of the true unfiltered SSR content.
      // Accepts the rowsContainer applyRowsTruncation() already resolved (when it ran) so
      // captureSkuServerSnapshot() doesn't have to run the exact same
      // `.main-products-grid__results .nu-products-table__body` query a second time.
      const captureSkuSnapshotAndUpdateCount = (rowsContainer) => {
        this.captureSkuServerSnapshot(rowsContainer);
        if (this.productsTotal) {
          this.handleProductsCountUpdate({
            detail: { count: this.productsTotal.getAttribute('data-base-count') || '0' },
          });
        }
      };

      // No spec-filters container means nu--spec-filters.js never applies client-side rows
      // truncation on its own. If the URL already carries a nu_rows override on cold load, the
      // SSR-rendered grid still reflects the section's default page size (Liquid can't read
      // arbitrary query params — see nu--pagination.liquid), so correct it once up front here.
      // The snapshot/count-update must wait for this to fully settle (not just be kicked off) —
      // captureSkuServerSnapshot() aborts any in-flight skuGlobalFetchController, and
      // applyRowsTruncation() may still be awaiting its own fetch through that very controller;
      // running them concurrently would abort a fetch its own (still-executing) caller hasn't
      // been given a chance to use yet.
      if (!this.hasNuSpecFilters() && this.filtersLite?.getQueryParam?.('nu_rows')) {
        // Covers the server-rendered "no products" fallback row while this resolves — without
        // it, a page/nu_rows combination the default SSR page size can't satisfy (H-34) briefly
        // shows that fallback as if it were the final result instead of still loading.
        this.setRowsLoadingStatusVisible(true);
        // On rejection, call with no argument (not the rejection reason) so
        // captureSkuServerSnapshot() falls back to querying for itself instead of treating
        // the error object as if it were a resolved rowsContainer.
        this.applyRowsTruncation().then(
          (rowsContainer) => {
            this.setRowsLoadingStatusVisible(false);
            captureSkuSnapshotAndUpdateCount(rowsContainer);
          },
          () => {
            this.setRowsLoadingStatusVisible(false);
            captureSkuSnapshotAndUpdateCount();
          }
        );
      } else {
        captureSkuSnapshotAndUpdateCount();
      }

      if (this.skuSearchInput) {
        this.onSkuSearchInput = this.handleSkuSearchInput.bind(this);
        this.skuSearchInput.addEventListener('input', this.onSkuSearchInput);
        this.toggleSkuSearchClearButton();
        this.toggleSkuSearchSubmitButton();
        if (this.getSkuSearchQuery()) {
          this.broadcastSkuSearchQuery({ busy: false });
        }
      }
      if (this.skuSearchForm) {
        this.onSkuSearchSubmit = this.handleSkuSearchSubmit.bind(this);
        this.skuSearchForm.addEventListener('submit', this.onSkuSearchSubmit);
      }

      if (this.skuSearchClearBtn) {
        this.onSkuSearchClear = this.handleSkuSearchClear.bind(this);
        this.skuSearchClearBtn.addEventListener('click', this.onSkuSearchClear);
      }

      if (this.rowsSelect) {
        this.rowsSelect.addEventListener('change', this.onRowsPerPageChange);
      }
      this.syncRowsSelectValue();

      // SSR-rendered pagination links (nu--pagination.liquid) never carry nu_rows or sort_by —
      // Liquid can't read arbitrary query params, and its first-page/page-number/last-page
      // links are built from collection.url alone (only the prev/next arrows use Shopify's own
      // paginate.previous/next.url, which already preserve the full query string) — so a
      // changed rows-per-page or sort order is silently dropped the moment someone clicks one
      // of those three link types. Client-side pagination rebuilds (renderClientPagination /
      // renderRowsClientPagination) already preserve everything correctly by cloning
      // window.location.href, but the plain SSR markup is what's shown on first paint and
      // whenever it gets reverted to (e.g. showColdLoadError() restoring
      // originalPaginationHtml) — a single delegated listener here, copying every current
      // param except page, stays correct across every one of those cases and every param
      // (not just nu_rows) instead of having to patch hrefs at each render site.
      this.onPaginationLinkClick = (event) => {
        const link = event.target.closest(
          '.main-products-grid__results a.pagination__page-link, .main-products-grid__results a.pagination__arrow'
        );
        if (!link || !link.href) return;
        const currentParams = new URL(window.location.href).searchParams;
        const linkUrl = new URL(link.href, window.location.href);
        let changed = false;
        currentParams.forEach((value, key) => {
          if (key === 'page') return;
          if (linkUrl.searchParams.has(key)) return;
          linkUrl.searchParams.set(key, value);
          changed = true;
        });
        if (!changed) return;
        link.href = linkUrl.toString();
      };
      document.addEventListener('click', this.onPaginationLinkClick);

      if (this.tableScroller) {
        this.tableScroller.addEventListener('scroll', this.onTableScroll, { passive: true });
        this.handleTableScroll();
      }
      this.setupFloatingTableHead();
      this.setupTableDragScroll();
      this.setupVirtualScrollbar();
      this.setupTableResizeObserver();
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
      window.addEventListener('resize', this.onWindowResize);
      window.addEventListener('keydown', this.onWindowKeydown);
      this.handleFloatingHeadPosition(true);

      if (this.mobileFiltersOpenBtn) {
        this.updateMobileFiltersButtonState();
        this.onMobileFiltersOpenClick = this.openMobileFilters.bind(this);
        this.mobileFiltersOpenBtn.addEventListener('click', this.onMobileFiltersOpenClick);
      }
      if (this.mobileFiltersCloseBtn) {
        this.onMobileFiltersCloseClick = this.closeMobileFilters.bind(this);
        this.mobileFiltersCloseBtn.addEventListener('click', this.onMobileFiltersCloseClick);
      }
      this.setupMobileFiltersAccessibility();
      this.setupMobileFilterCards();
      this.syncMobileFiltersAccessibility();

      if (this.layoutSwitcher) {
        this.onLayoutChange = this.handleLayoutChange.bind(this);
        this.layoutSwitcher.addEventListener('change', this.onLayoutChange);

        // Set the layout to the layout in localstorage
        const layout = theme.storageUtil.get('products-layout');
        if (layout && layout === 'list') {
          const toggle = document.getElementById(`${layout}-layout-opt`);
          if (toggle) toggle.click();
          this.handleLayoutChange(null, layout);
        }
      }

      setTimeout(() => {
        this.classList.add('transition-ready');
        if (this.productsGrid) {
          this.productsGrid.classList.add('transition-ready');
        }
      }, 500);

      if (this.productsGrid?.dataset?.compare === 'true') {
        this.loadCompareResources();
      }

      if (this.sortBy) {
        this.sortBy.addEventListener('change', ProductsToolbar.handleSortByChange);
      }
    }

    disconnectedCallback() {
      // Mobile drawer scroll lock is applied to <html>/<body>, not to this element — if a
      // Theme Editor reload disconnects this component while the drawer is open, nothing
      // else would ever clear it, permanently locking page scroll. closeMobileFilters() is
      // a no-op if the drawer isn't open, so it's always safe to call here.
      this.closeMobileFilters();
      if (this.skuSearchInput && this.onSkuSearchInput) {
        this.skuSearchInput.removeEventListener('input', this.onSkuSearchInput);
      }
      if (this.skuSearchForm && this.onSkuSearchSubmit) {
        this.skuSearchForm.removeEventListener('submit', this.onSkuSearchSubmit);
      }
      if (this.skuSearchClearBtn && this.onSkuSearchClear) {
        this.skuSearchClearBtn.removeEventListener('click', this.onSkuSearchClear);
      }
      if (this.mobileFiltersOpenBtn && this.onMobileFiltersOpenClick) {
        this.mobileFiltersOpenBtn.removeEventListener('click', this.onMobileFiltersOpenClick);
      }
      if (this.mobileFiltersCloseBtn && this.onMobileFiltersCloseClick) {
        this.mobileFiltersCloseBtn.removeEventListener('click', this.onMobileFiltersCloseClick);
      }
      if (this.layoutSwitcher && this.onLayoutChange) {
        this.layoutSwitcher.removeEventListener('change', this.onLayoutChange);
      }
      if (this.onPaginationLinkClick) {
        document.removeEventListener('click', this.onPaginationLinkClick);
      }
      if (this.skuGlobalFetchController) {
        this.skuGlobalFetchController.abort();
        this.skuGlobalFetchController = null;
      }
      this.skuGlobalFetchPromise = null;
      if (this.r4qRefreshTimer) {
        window.clearTimeout(this.r4qRefreshTimer);
        this.r4qRefreshTimer = null;
      }
      if (this.r4qBusyClearTimer) {
        window.clearTimeout(this.r4qBusyClearTimer);
        this.r4qBusyClearTimer = null;
      }
      if (this.r4qBusyShowTimer) {
        window.clearTimeout(this.r4qBusyShowTimer);
        this.r4qBusyShowTimer = null;
      }
      this.setGlobalBusyState(false);
      if (this.floatingHeadRafId) {
        window.cancelAnimationFrame(this.floatingHeadRafId);
        this.floatingHeadRafId = null;
      }
      this.floatingHeadRafPending = false;
      if (this.onProductsCountUpdate) {
        document.removeEventListener('nu:products-count:update', this.onProductsCountUpdate);
      }
      if (this.onNuSpecFiltersReady) {
        document.removeEventListener('nu:spec-filters:ready', this.onNuSpecFiltersReady);
      }
      if (this.onR4QRefreshRequested) {
        document.removeEventListener('nu:r4q:refresh', this.onR4QRefreshRequested);
      }
      if (this.onR4QRefreshDone) {
        document.removeEventListener('nu:r4q:refresh:done', this.onR4QRefreshDone);
      }
      if (this.onR4QBusyTriggerClick) {
        document.removeEventListener('click', this.onR4QBusyTriggerClick, true);
      }
      if (this.tableScroller && this.onTableScroll) {
        this.tableScroller.removeEventListener('scroll', this.onTableScroll);
      }
      if (this.tableScroller && this.onTableScrollerPointerDown) {
        this.tableScroller.removeEventListener('pointerdown', this.onTableScrollerPointerDown);
      }
      this.cancelTableDragScroll();
      if (this.tableResizeObserver) {
        this.tableResizeObserver.disconnect();
        this.tableResizeObserver = null;
      }
      if (this.virtualScrollbarEl) {
        if (this.onVirtualScrollbarScroll) {
          this.virtualScrollbarEl.removeEventListener('scroll', this.onVirtualScrollbarScroll);
        }
        this.virtualScrollbarEl.remove();
        this.virtualScrollbarEl = null;
        this.virtualScrollbarInnerEl = null;
      }
      if (this.onWindowScroll) {
        window.removeEventListener('scroll', this.onWindowScroll);
      }
      if (this.onWindowResize) {
        window.removeEventListener('resize', this.onWindowResize);
      }
      if (this.onWindowKeydown) {
        window.removeEventListener('keydown', this.onWindowKeydown);
      }
      if (this.floatingHeadEl) {
        this.floatingHeadEl.remove();
        this.floatingHeadEl = null;
      }
    }

    static formatCount(value) {
      if (window.NuFiltersLite?.formatCount) {
        return window.NuFiltersLite.formatCount(value);
      }
      const number = Number(value);
      if (!Number.isFinite(number)) return '0';
      return new Intl.NumberFormat().format(number);
    }

    // The [count] placeholder (rather than a literal template string here) is what lets
    // this stay in sync with the translated products.filtering.nu_products_total copy
    // rendered server-side — see the data-nu-products-total-template attribute this reads.
    // The attribute is read here, at the point of use, rather than cached on `this` in
    // init(), so the value never becomes component state.
    setProductsTotalText(count) {
      if (!this.productsTotal) return;
      const template = ProductsToolbar.normalizeTotalTemplate(
        this.productsTotal.getAttribute('data-nu-products-total-template')
      );
      this.productsTotal.textContent = template.replace('[count]', ProductsToolbar.formatCount(count));
    }

    // Accepts the server-rendered copy only if it is a short plain-text string that still
    // contains the [count] placeholder; anything else falls back to the default copy.
    static normalizeTotalTemplate(raw) {
      const fallback = 'Total [count] Items';
      const text = String(raw || '').trim();
      if (!text || text.length > 100 || !text.includes('[count]') || /[<>]/.test(text)) return fallback;
      return text;
    }

    handleProductsCountUpdate(evt) {
      if (!this.productsTotal) return;
      const nextCount = Number(evt?.detail?.count);
      if (!Number.isFinite(nextCount)) return;
      this.baseResultsCount = nextCount;
      if (this.hasNuSpecFilters()) {
        this.setProductsTotalText(nextCount);
        return;
      }
      if (this.getSkuSearchQuery()) {
        this.applySkuSearchFilter();
        return;
      }
      this.setProductsTotalText(nextCount);
    }

    hasNuSpecFilters() {
      return Boolean(document.querySelector('[data-nu-spec-filters]'));
    }

    broadcastSkuSearchQuery(options = {}) {
      if (!this.hasNuSpecFilters()) return false;
      const busy = Boolean(options.busy);
      document.dispatchEvent(new CustomEvent('nu:sku-search:update', {
        detail: {
          query: this.getSkuSearchQuery(),
          busy,
        },
      }));
      return true;
    }

    getSkuSearchQuery() {
      return String(this.skuSearchQuery || '').trim().toLowerCase();
    }

    getSkuSearchInputQuery() {
      return (this.skuSearchInput?.value || '').trim().toLowerCase();
    }

    getSkuQueryFromUrl() {
      try {
        const url = new URL(window.location.href);
        return (url.searchParams.get('nu_sku') || '').trim();
      } catch (error) {
        return '';
      }
    }

    syncSkuQueryToUrlForOffMode(query) {
      if (this.hasNuSpecFilters()) return;
      try {
        const url = new URL(window.location.href);
        const normalizedQuery = String(query || '').trim();
        if (normalizedQuery) {
          url.searchParams.set('nu_sku', normalizedQuery);
        } else {
          url.searchParams.delete('nu_sku');
        }
        url.searchParams.delete('page');
        history.replaceState({}, '', url.toString());
      } catch (error) {
        // Ignore URL sync errors and continue filtering.
      }
    }

    setGlobalBusyState(isBusy) {
      if (!this.layoutRoot) return;
      if (isBusy) {
        this.r4qBusyVisibleSince = Date.now();
      } else {
        this.r4qBusyVisibleSince = 0;
      }
      this.layoutRoot.setAttribute('data-nu-busy', isBusy ? 'true' : 'false');
      document.body.classList.toggle('nu-global-refreshing', Boolean(isBusy));
    }

    beginR4QBusy(version, options = {}) {
      const forceVisible = Boolean(options.forceVisible);
      if (this.r4qBusyShowTimer) {
        window.clearTimeout(this.r4qBusyShowTimer);
        this.r4qBusyShowTimer = null;
      }
      this.setGlobalBusyState(false);
      const showDelay = forceVisible ? 0 : this.r4qBusyShowDelayMs;
      this.r4qBusyShowTimer = window.setTimeout(() => {
        this.r4qBusyShowTimer = null;
        if (version !== this.r4qBusyVersion) return;
        this.setGlobalBusyState(true);
      }, showDelay);
    }

    endR4QBusy(version) {
      // A falsy version carries no assertion about which busy state it belongs to (see
      // handleR4QRefreshDone) — treat it as "don't touch anything" rather than "matches
      // whatever is currently busy".
      if (!version || version !== this.r4qBusyVersion) return;
      if (this.r4qBusyShowTimer) {
        window.clearTimeout(this.r4qBusyShowTimer);
        this.r4qBusyShowTimer = null;
      }
      if (this.r4qBusyClearTimer) {
        window.clearTimeout(this.r4qBusyClearTimer);
        this.r4qBusyClearTimer = null;
      }
      const isVisible = this.layoutRoot?.getAttribute('data-nu-busy') === 'true';
      if (!isVisible) {
        this.setGlobalBusyState(false);
        return;
      }
      const visibleElapsed = Date.now() - (this.r4qBusyVisibleSince || 0);
      const remain = Math.max(0, this.r4qBusyMinVisibleMs - visibleElapsed);
      if (remain <= 0) {
        this.setGlobalBusyState(false);
        return;
      }
      this.r4qBusyClearTimer = window.setTimeout(() => {
        this.r4qBusyClearTimer = null;
        this.setGlobalBusyState(false);
      }, remain);
    }

    handleR4QRefreshDone(event) {
      const doneVersion = Number(event?.detail?.version || 0);
      // A version of 0 means this completion isn't tied to any real busy-overlay request
      // (boot-time/BSS-ready refreshes in nu--collection-r4q.js don't carry one) — it must
      // not be treated as "matches everything" and clear an unrelated in-progress overlay.
      if (doneVersion) {
        if (doneVersion !== this.r4qBusyVersion) return;
        this.endR4QBusy(doneVersion);
      }
      // Keep a warmed snapshot after BSS settles so clearing SKU search
      // can restore visible prices/tables without long blank states.
      if (!this.getSkuSearchQuery()) {
        // R4Q settling is not a state transition (no SKU search cleared, no page change) —
        // it has no reason to invalidate an unrelated in-flight cross-page fetch. R4Q's boot
        // sequence dispatches nu:r4q:refresh:done (with version 0, see above) within tens of
        // ms of page load — the same window as the cold-load rows-per-page correction
        // (applyRowsTruncation() -> getGlobalRenderedProductsForSku(), see H-34 in
        // .doc/full-project-review) kicking off its own cross-page fetch. Aborting that fetch
        // from here left the page permanently stuck on the SSR "no products" state once H-34's
        // Liquid fix gave applyRowsTruncation() a container to correct into: the fetch died
        // before any page could resolve, and nothing ever retried it. Snapshot only; never
        // abort the global fetch from this call site.
        this.captureSkuServerSnapshot(undefined, { abortGlobalFetch: false });
      }
    }

    handleR4QRefreshRequested(event) {
      const isBusyRequest = Boolean(event?.detail?.busy);
      if (!isBusyRequest) return;
      const requestVersion = Number(event?.detail?.version || 0);
      const forceVisible = Boolean(event?.detail?.forceVisible);
      if (
        requestVersion
        && requestVersion === this.r4qBusyVersion
        && (this.r4qBusyShowTimer || this.layoutRoot?.dataset?.nuBusy === 'true')
      ) {
        return;
      }
      if (requestVersion > this.r4qBusyVersion) {
        this.r4qBusyVersion = requestVersion;
      } else if (!requestVersion) {
        this.r4qBusyVersion = this.filtersLite?.nextR4QRefreshVersion() ?? (this.r4qBusyVersion + 1);
      }
      this.beginR4QBusy(this.r4qBusyVersion, { forceVisible });
      this.armR4QBusyFailsafe(this.r4qBusyVersion);
    }

    // Fallback safety net: nu:r4q:refresh:done is only ever dispatched by refreshNativeR4Q()
    // in nu--collection-r4q.js, which returns before its try/finally whenever the merchant's
    // "Add to quote" mode isn't "bss" or native R4Q placement isn't enabled — leaving this
    // overlay stuck forever with no other clear path.
    armR4QBusyFailsafe(version) {
      if (this.r4qBusyClearTimer) window.clearTimeout(this.r4qBusyClearTimer);
      this.r4qBusyClearTimer = window.setTimeout(() => {
        this.r4qBusyClearTimer = null;
        this.endR4QBusy(version);
      }, 6000);
    }

    handleR4QBusyTriggerClick(event) {
      const triggerEl = event?.target?.closest?.(
        '[data-nu-spec-apply], [data-nu-spec-clear-all], [data-nu-spec-applied-remove]'
      );
      if (!triggerEl) return;
      let triggerType = 'unknown';
      if (triggerEl.hasAttribute('data-nu-spec-apply')) triggerType = 'spec-apply';
      else if (triggerEl.hasAttribute('data-nu-spec-clear-all')) triggerType = 'spec-clear-all';
      else if (triggerEl.hasAttribute('data-nu-spec-applied-remove')) triggerType = 'spec-applied-remove';
      const debugEnabled = this.layoutRoot?.dataset?.nuPerfLog === 'true' || Boolean(window?.Shopify?.designMode);
      if (debugEnabled) {
        console.debug('[nu][r4q][busy]', 'busy overlay queued', {
          trigger: triggerType,
          text: (triggerEl.textContent || '').trim(),
        });
      }
      this.r4qBusyNextRefresh = true;
    }

    consumeBusyIntent(defaultValue = false) {
      const next = this.r4qBusyNextRefresh;
      this.r4qBusyNextRefresh = false;
      return Boolean(next || defaultValue);
    }

    requestR4QRefresh({ busy = false } = {}) {
      const useBusy = this.consumeBusyIntent(busy);
      if (this.r4qRefreshTimer) {
        window.clearTimeout(this.r4qRefreshTimer);
      }
      this.r4qRefreshTimer = window.setTimeout(() => {
        this.r4qRefreshTimer = null;
        this.r4qBusyVersion = this.filtersLite?.nextR4QRefreshVersion() ?? (this.r4qBusyVersion + 1);
        const version = this.r4qBusyVersion;
        if (useBusy) {
          this.beginR4QBusy(version, { forceVisible: true });
          this.armR4QBusyFailsafe(version);
        }
        document.dispatchEvent(new CustomEvent('nu:r4q:refresh', {
          detail: { version, busy: useBusy, forceVisible: useBusy },
        }));
      }, this.r4qRefreshDelayMs);
    }

    toggleSkuSearchClearButton() {
      if (!this.skuSearchClearBtn) return;
      this.skuSearchClearBtn.hidden = this.getSkuSearchInputQuery().length === 0;
    }

    toggleSkuSearchSubmitButton() {
      if (!this.skuSearchSubmitBtn) return;
      this.skuSearchSubmitBtn.disabled = this.getSkuSearchInputQuery().length === 0;
    }

    // Despite the name, data-product-skus now holds product.title (see the liquid comment at
    // its source) — "Search Part Number" matches what's shown in the Part Number column, which
    // is the title, not variant.sku. Left as "Skus" throughout this file/nu--spec-filters.js to
    // avoid a wide rename of the shared handleToSkus cache-object shape both files pass around.
    static getProductSkus(cardEl) {
      return String(cardEl?.dataset?.productSkus || '')
        .toLowerCase()
        .split('|')
        .map((sku) => sku.trim())
        .filter(Boolean);
    }

    static isProductVisibleByOtherFilters(cardEl) {
      return !cardEl.classList.contains('nu-spec-hidden');
    }

    handleSkuSearchInput() {
      this.toggleSkuSearchClearButton();
      this.toggleSkuSearchSubmitButton();
    }

    handleSkuSearchSubmit(event) {
      if (event) event.preventDefault();
      if (this.skuSearchSubmitBtn?.disabled) return;
      this.skuSearchQuery = this.getSkuSearchInputQuery();
      this.syncSkuQueryToUrlForOffMode(this.skuSearchQuery);
      this.toggleSkuSearchClearButton();
      this.toggleSkuSearchSubmitButton();
      if (this.broadcastSkuSearchQuery({ busy: true })) return;
      this.applySkuSearchFilter({ busyOverlay: true });
    }

    handleSkuSearchClear() {
      if (!this.skuSearchInput) return;
      this.skuSearchInput.value = '';
      this.skuSearchQuery = '';
      this.syncSkuQueryToUrlForOffMode('');
      this.toggleSkuSearchClearButton();
      this.toggleSkuSearchSubmitButton();
      if (this.broadcastSkuSearchQuery({ busy: false })) {
        this.skuSearchInput.focus();
        return;
      }
      this.applySkuSearchFilter();
      this.skuSearchInput.focus();
    }

    async handleRowsPerPageChange(evt) {
      const nextRows = String(evt.target.value || '').trim();
      if (!nextRows) return;
      const url = new URL(window.location.href);
      const currentRows = String(url.searchParams.get('nu_rows') || '').trim();
      if (currentRows === nextRows) return;
      if (this.rowsChangeInFlight) return;

      this.rowsChangeInFlight = true;
      if (this.rowsSelect) this.rowsSelect.disabled = true;
      // Route through the same version-tracked busy overlay R4Q refreshes use (instead of the
      // bare setGlobalBusyState toggle) so a still-in-flight R4Q refresh's overlay can't be
      // prematurely hidden by this call finishing first, or vice versa.
      this.r4qBusyVersion = this.filtersLite?.nextR4QRefreshVersion() ?? (this.r4qBusyVersion + 1);
      const busyVersion = this.r4qBusyVersion;
      this.beginR4QBusy(busyVersion, { forceVisible: true });

      url.searchParams.set('nu_rows', nextRows);
      url.searchParams.delete('page');
      history.pushState({}, '', url.toString());

      try {
        // No spec-filters container == nu--spec-filters.js never loaded (collection has no
        // Product Group, is the automatic "all" collection, or filtering is off in Theme
        // Editor) == nobody will ever dispatch nu:rows-per-page:done. Dispatching and waiting
        // in that case just burns the full waitForRowsPerPageDone() timeout for no effect;
        // fall back to the client-side truncation this toolbar already owns instead.
        if (this.hasNuSpecFilters()) {
          const requestId = `rows-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          document.dispatchEvent(new CustomEvent('nu:rows-per-page:change', {
            detail: {
              rows: Number(nextRows),
              requestId,
              busyOverlay: true,
            },
          }));
          await this.waitForRowsPerPageDone(requestId);
        } else if (this.getSkuSearchQuery()) {
          // An active SKU search must keep taking effect — applyRowsTruncation() rebuilds
          // pagination from the SSR-only data-nu-filtered-count total, which never reflects
          // an applied SKU search, and would silently revert to the unfiltered product list.
          // applySkuSearchFilter()'s global-search path re-derives the current rows-per-page
          // from the URL (already updated above), so it stays correct for the new page size.
          await this.applySkuSearchFilter({ busyOverlay: false });
        } else {
          await this.applyRowsTruncation();
        }
      } finally {
        this.endR4QBusy(busyVersion);
        if (this.rowsSelect) this.rowsSelect.disabled = false;
        this.rowsChangeInFlight = false;
      }
    }

    waitForRowsPerPageDone(requestId, timeoutMs = 12000) {
      return new Promise((resolve) => {
        let settled = false;
        let timerId = null;
        const cleanup = () => {
          if (timerId) {
            window.clearTimeout(timerId);
            timerId = null;
          }
          document.removeEventListener('nu:rows-per-page:done', handleDone);
        };
        const settle = () => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve();
        };
        const handleDone = (event) => {
          if (event?.detail?.requestId !== requestId) return;
          settle();
        };
        document.addEventListener('nu:rows-per-page:done', handleDone);
        timerId = window.setTimeout(() => {
          settle();
        }, timeoutMs);
      });
    }

    loadCompareResources() {
      const resourcesTag = document.getElementById('compare-resources');
      if (!resourcesTag) return;
      const resources = JSON.parse(resourcesTag.textContent || '[]');
      resources.forEach((resource) => {
        if (resource.type === 'js') {
          const script = document.createElement('script');
          script.src = resource.path;
          document.head.appendChild(script);
        } else if (resource.type === 'css') {
          const link = document.createElement('link');
          link.href = resource.path;
          link.type = 'text/css';
          link.rel = 'stylesheet';
          document.head.appendChild(link);
        }
      });
      resourcesTag.remove();
    }

    syncRowsSelectValue() {
      if (!this.rowsSelect) return;
      try {
        const url = new URL(window.location.href);
        const nuRows = url.searchParams.get('nu_rows');
        if (!nuRows) return;
        const matchingOption = this.rowsSelect.querySelector(`option[value="${nuRows}"]`);
        if (matchingOption) this.rowsSelect.value = nuRows;
      } catch (e) {
        // Ignore URL parse errors
      }
    }

    async applyRowsTruncation() {
      // Several call sites (client-rendered pagination link clicks, rows-per-page change)
      // fire this without awaiting or de-duping each other — the only await below can let
      // an earlier, slower call's DOM writes land after a newer call already finished,
      // showing content for the wrong page relative to the URL. Only the most recent call
      // is allowed to proceed past that await.
      const truncationToken = ++this.rowsTruncationToken;
      const rowsPerPage = this.getRowsPerPageFromUrl();
      const cards = Array.from(document.querySelectorAll('.main-products-grid__results [data-product-handle]'));
      const rowsContainer = document.querySelector('.main-products-grid__results .nu-products-table__body')
        || document.querySelector('.main-products-grid__results > ul');
      const paginationWrap = this.getOrCreateRowsPaginationWrap();
      const total = this.getTotalItemsForRowsPagination(cards.length);
      if (total === 0) return rowsContainer;

      let renderedSource = ProductsToolbar.extractRenderedProducts(document);
      const renderedCount = renderedSource.orderedHandles.length;
      const currentPage = this.getCurrentPageFromUrl();
      const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
      const safePage = Math.min(Math.max(1, currentPage), totalPages);
      const startIndex = (safePage - 1) * rowsPerPage;
      const endIndex = startIndex + rowsPerPage;
      const minRequiredCount = rowsPerPage >= total ? total : Math.min(total, endIndex);
      const shouldLoadGlobal = renderedCount < minRequiredCount;

      if (shouldLoadGlobal) {
        try {
          const globalRendered = await this.getGlobalRenderedProductsForSku();
          if (globalRendered?.orderedHandles?.length) {
            renderedSource = globalRendered;
          }
        } catch (error) {
          // Keep current DOM source as fallback.
        }
        if (truncationToken !== this.rowsTruncationToken) return rowsContainer;
      }
      const orderedHandles = renderedSource.orderedHandles || [];
      const totalForPaging = Math.max(total, orderedHandles.length);
      if (totalForPaging === 0) return rowsContainer;

      if (rowsPerPage >= totalForPaging) {
        if (rowsContainer && orderedHandles.length > 0) {
          rowsContainer.innerHTML = orderedHandles
            .map((handle) => renderedSource.handleToHtml?.[handle])
            .filter(Boolean)
            .join('');
          // Freshly-inserted rows are brand-new DOM nodes — any R4Q "Add to Quote" widget
          // markup they carry (bcp-r4q-button-wrapper) is unhydrated until
          // nu--collection-r4q.js patches/loads it again. Nothing else re-triggers that for
          // rows injected here (no MutationObserver watches this table), so without this the
          // button silently never appears on rows sourced from a cross-page fetch.
          this.requestR4QRefresh({ busy: false });
        }
        cards.forEach((card) => { card.hidden = false; });
        if (paginationWrap) paginationWrap.hidden = true;
        return rowsContainer;
      }

      const totalPagesWithRendered = Math.max(1, Math.ceil(totalForPaging / rowsPerPage));
      const safePageWithRendered = Math.min(Math.max(1, currentPage), totalPagesWithRendered);

      if (safePageWithRendered !== currentPage) {
        const url = new URL(window.location.href);
        if (safePageWithRendered <= 1) url.searchParams.delete('page');
        else url.searchParams.set('page', String(safePageWithRendered));
        history.replaceState({}, '', url.toString());
      }

      const startIndexWithRendered = (safePageWithRendered - 1) * rowsPerPage;
      const endIndexWithRendered = startIndexWithRendered + rowsPerPage;
      const pageHandles = orderedHandles.slice(startIndexWithRendered, endIndexWithRendered);
      if (rowsContainer && pageHandles.length > 0) {
        rowsContainer.innerHTML = pageHandles
          .map((handle) => renderedSource.handleToHtml?.[handle])
          .filter(Boolean)
          .join('');
        // See the identical comment above — freshly-inserted rows need R4Q to (re)patch them.
        this.requestR4QRefresh({ busy: false });
      } else {
        // Must use the *WithRendered bounds here, not startIndex/endIndex — those were
        // computed against `total` (SSR-only count) above, while this fallback walks `cards`,
        // which can include cross-page handles pulled in via orderedHandles. Using the stale
        // bounds mis-hides every card once orderedHandles.length > total.
        cards.forEach((card, index) => {
          card.hidden = index < startIndexWithRendered || index >= endIndexWithRendered;
        });
      }

      if (paginationWrap) {
        this.renderRowsClientPagination(paginationWrap, totalForPaging, safePageWithRendered, rowsPerPage);
      }
      return rowsContainer;
    }

    getOrCreateRowsPaginationWrap() {
      const existingNav = document.querySelector('.main-products-grid__results nav[aria-label]');
      if (existingNav) return existingNav;

      const paginationHost = document.querySelector('.main-products-grid__results .nu-products-table__pagination-wrap');
      if (!paginationHost) return null;

      const nav = document.createElement('nav');
      nav.setAttribute('aria-label', this.paginationLabel);
      paginationHost.appendChild(nav);
      return nav;
    }

    getTotalItemsForRowsPagination(fallback = 0) {
      const filteredCountEl = document.querySelector('.main-products-grid__results [data-nu-filtered-count]');
      const fromFilteredCount = Number(filteredCountEl?.getAttribute('data-nu-filtered-count') || '');
      if (Number.isFinite(fromFilteredCount) && fromFilteredCount > 0) return fromFilteredCount;

      const fromBaseCount = Number(this.productsTotal?.getAttribute('data-base-count') || '');
      if (Number.isFinite(fromBaseCount) && fromBaseCount > 0) return fromBaseCount;

      const totalText = String(this.productsTotal?.textContent || '');
      const parsedFromText = Number(totalText.replace(/[^\d]/g, ''));
      if (Number.isFinite(parsedFromText) && parsedFromText > 0) return parsedFromText;

      return fallback > 0 ? fallback : 0;
    }

    // Shared with nu--spec-filters.js via NuFiltersLite.getPaginationPageItems() (see
    // nu--filters-lite.js) so the two never diverge from each other or from
    // snippets/nu--pagination.liquid's start_page/end_page logic.
    getPaginationPageItems(currentPage, totalPages) {
      return this.filtersLite?.getPaginationPageItems(currentPage, totalPages) ?? [];
    }

    // Shared by renderRowsClientPagination() and renderSkuClientPagination() — both need
    // client-side pagination (no spec-filters container to hand this off to), and both
    // must look identical to the server-rendered snippets/nu--pagination.liquid markup
    // (same classes, first/last arrows, mobile "Page X / Y" indicator) or the pagination
    // visibly changes style the moment either one takes over. `pageDataAttr` is the only
    // thing that differs between callers — it's what each one's own click handler
    // intercepts, so a rows-pagination render and a SKU-search-pagination render already
    // bound on the same <nav> don't answer clicks meant for the other.
    renderClientPagination(paginationWrap, totalItems, currentPageNum, rowsPerPage, pageDataAttr) {
      if (!paginationWrap) return;
      const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
      const safeCurrentPage = Math.min(Math.max(1, currentPageNum), totalPages);

      if (totalPages <= 1) {
        paginationWrap.hidden = true;
        return;
      }

      paginationWrap.hidden = false;
      paginationWrap.setAttribute('aria-label', this.paginationLabel);

      const ul = document.createElement('ul');
      ul.className = 'pagination relative flex flex-wrap justify-center items-center justify-between w-full mx-auto js-pagination';

      const buildArrowItem = ({
        arrowModifier, extraItemClass = '', extraLinkClass = '', isHidden, targetPage, iconHtml, label,
      }) => {
        const li = document.createElement('li');
        li.className = `pagination__item pagination__item--arrow${extraItemClass}`;
        if (isHidden) li.hidden = true;
        const link = document.createElement('a');
        // pagination__arrow--first/prev/next/last carries the spacing rules in main.css
        // and nu--collection-layout.css — without it the arrows are still functional but
        // visibly mis-spaced compared to the server-rendered nu--pagination.liquid markup.
        link.className = `pagination__arrow pagination__arrow--${arrowModifier}${extraLinkClass}`;
        if (isHidden) {
          link.setAttribute('role', 'link');
          link.setAttribute('aria-disabled', 'true');
        } else {
          link.href = '#';
          link.setAttribute(pageDataAttr, String(targetPage));
        }
        const iconSpan = document.createElement('span');
        iconSpan.className = 'pagination__arrow-icon';
        iconSpan.setAttribute('aria-hidden', 'true');
        iconSpan.innerHTML = iconHtml;
        const labelSpan = document.createElement('span');
        labelSpan.className = 'visually-hidden';
        labelSpan.textContent = label;
        link.append(iconSpan, labelSpan);
        li.appendChild(link);
        return li;
      };

      ul.appendChild(buildArrowItem({
        arrowModifier: 'first',
        extraItemClass: ' pagination__item--arrow-first',
        extraLinkClass: ' hover:lighten flex text-current items-center has-ltr-icon',
        isHidden: safeCurrentPage <= 1,
        targetPage: 1,
        iconHtml: PAGINATION_FIRST_ICON_SVG,
        label: this.paginationFirstPageLabel,
      }));
      ul.appendChild(buildArrowItem({
        arrowModifier: 'prev',
        extraLinkClass: ' hover:lighten flex text-current items-center has-ltr-icon',
        isHidden: safeCurrentPage <= 1,
        targetPage: safeCurrentPage - 1,
        iconHtml: PAGINATION_PREV_ICON_SVG,
        label: this.paginationPreviousLabel,
      }));

      const positionLi = document.createElement('li');
      positionLi.className = 'pagination__item text-center md:hidden font-bold';
      positionLi.textContent = this.paginationPositionTemplate
        .replace('[current]', String(safeCurrentPage))
        .replace('[total]', String(totalPages));
      ul.appendChild(positionLi);

      this.getPaginationPageItems(safeCurrentPage, totalPages).forEach((pageNum) => {
        const li = document.createElement('li');
        li.className = 'hidden md:block';
        if (pageNum === safeCurrentPage) {
          const current = document.createElement('span');
          current.className = 'pagination__page-current font-bold block leading-none';
          current.textContent = String(pageNum);
          li.appendChild(current);
        } else {
          const link = document.createElement('a');
          link.className = 'pagination__page-link text-current block leading-none';
          link.href = '#';
          link.textContent = String(pageNum);
          link.setAttribute(pageDataAttr, String(pageNum));
          li.appendChild(link);
        }
        ul.appendChild(li);
      });

      ul.appendChild(buildArrowItem({
        arrowModifier: 'next',
        extraLinkClass: ' hover:lighten flex justify-end text-current items-center has-ltr-icon js-pagination-load-more',
        isHidden: safeCurrentPage >= totalPages,
        targetPage: safeCurrentPage + 1,
        iconHtml: PAGINATION_NEXT_ICON_SVG,
        label: this.paginationNextLabel,
      }));
      ul.appendChild(buildArrowItem({
        arrowModifier: 'last',
        extraItemClass: ' pagination__item--arrow-last',
        extraLinkClass: ' hover:lighten flex justify-end text-current items-center has-ltr-icon',
        isHidden: safeCurrentPage >= totalPages,
        targetPage: totalPages,
        iconHtml: PAGINATION_LAST_ICON_SVG,
        label: this.paginationLastPageLabel,
      }));

      paginationWrap.innerHTML = '';
      paginationWrap.appendChild(ul);
    }

    renderRowsClientPagination(paginationWrap, totalItems, currentPageNum, rowsPerPage) {
      this.renderClientPagination(paginationWrap, totalItems, currentPageNum, rowsPerPage, 'data-nu-rows-page');
      if (!paginationWrap || paginationWrap.dataset.nuRowsPaginationBound === 'true') return;
      paginationWrap.addEventListener('click', (event) => {
        const link = event.target.closest('[data-nu-rows-page]');
        if (!link) return;
        event.preventDefault();
        const targetPage = Number(link.getAttribute('data-nu-rows-page'));
        if (!Number.isFinite(targetPage) || targetPage < 1) return;
        const url = new URL(window.location.href);
        if (targetPage <= 1) url.searchParams.delete('page');
        else url.searchParams.set('page', String(targetPage));
        // A real navigation instead of client truncation (H-33): applyRowsTruncation() rebuilds
        // its product list from whatever is currently in the DOM, but that DOM was already
        // narrowed down to just the previous page's slice by the last truncation call — every
        // further click has to re-fetch cross-page data to recover, and when that doesn't land
        // cleanly the table renders empty until a real reload. A full navigation always gets a
        // correct, complete SSR render for the target page, with no client-side state to drift.
        window.location.href = url.toString();
      });
      paginationWrap.dataset.nuRowsPaginationBound = 'true';
    }

    handleNuSpecFiltersReady() {
      this.updateMobileFiltersButtonState(true);
    }

    updateMobileFiltersButtonState(isReady = false) {
      if (!this.mobileFiltersOpenBtn) return;
      const hasNuSpecFilters = Boolean(document.querySelector('[data-nu-spec-filters]'));
      if (!hasNuSpecFilters) {
        this.mobileFiltersOpenBtn.disabled = false;
        return;
      }
      if (isReady) {
        this.mobileFiltersOpenBtn.disabled = false;
      }
    }

    handleTableScroll() {
      if (!this.tableScroller) return;
      const maxScrollLeft = Math.max(this.tableScroller.scrollWidth - this.tableScroller.clientWidth, 0);
      const hasRightOverflow = maxScrollLeft - this.tableScroller.scrollLeft > 1;
      this.tableScroller.classList.toggle('is-scrolled-x', hasRightOverflow);
      this.tableScroller.classList.toggle('is-drag-scrollable', maxScrollLeft > 0);
      if (this.floatingHeadEl) {
        this.floatingHeadEl.classList.toggle('is-scrolled-x', hasRightOverflow);
      }
      if (this.virtualScrollbarEl && !this.virtualScrollbarEl.hidden && !this.isSyncingVirtualScrollbar) {
        this.isSyncingVirtualScrollbar = true;
        this.virtualScrollbarEl.scrollLeft = this.tableScroller.scrollLeft;
        this.isSyncingVirtualScrollbar = false;
      }
      this.handleFloatingHeadPosition();
    }

    isMobileViewport() {
      return window.matchMedia('(max-width: 768.98px)').matches;
    }

    setupMobileFiltersAccessibility() {
      if (!this.filtersColumn || !this.mobileFiltersOpenBtn) return;
      if (!this.filtersColumn.id) {
        this.filtersColumn.id = 'nu-mobile-filters-panel';
      }
      this.mobileFiltersOpenBtn.setAttribute('aria-controls', this.filtersColumn.id);
    }

    syncMobileFiltersAccessibility() {
      const isMobile = this.isMobileViewport();
      const isOpenMobile = Boolean(this.filtersColumn?.classList.contains('is-open-mobile'));

      if (this.filtersColumn) {
        // Desktop 固定顯示，不應保持 aria-hidden=true。
        this.filtersColumn.setAttribute('aria-hidden', String(isMobile ? !isOpenMobile : false));
      }
      if (this.mobileFiltersOpenBtn) {
        this.mobileFiltersOpenBtn.setAttribute('aria-expanded', String(isMobile && isOpenMobile));
      }
    }

    async openMobileFilters() {
      if (!this.filtersColumn || !this.isMobileViewport()) return;

      const specFiltersEl = this.filtersColumn.querySelector('[data-nu-spec-filters]');
      if (specFiltersEl && typeof specFiltersEl.nuSpecEnsureDataLoaded === 'function') {
        // Cross-page filter data is fetched lazily on mobile (see nu--spec-filters.js),
        // so the first open needs to wait for it. Show the full-page busy state instead
        // of revealing a drawer with incomplete filter options. Subsequent opens resolve
        // this instantly since the fetch only ever runs once.
        // Version-tracked like handleRowsPerPageChange() — see the comment there — so this
        // can't clobber (or be clobbered by) an unrelated in-flight R4Q busy overlay.
        this.r4qBusyVersion = this.filtersLite?.nextR4QRefreshVersion() ?? (this.r4qBusyVersion + 1);
        const busyVersion = this.r4qBusyVersion;
        this.beginR4QBusy(busyVersion, { forceVisible: true });
        try {
          await specFiltersEl.nuSpecEnsureDataLoaded();
        } finally {
          this.endR4QBusy(busyVersion);
        }
        // The viewport may have changed while we were waiting (e.g. a rotation);
        // don't reveal the mobile drawer if we're no longer in a mobile layout.
        if (!this.isMobileViewport()) return;
        // The fetch can fail while this await was pending — the error layout already
        // hides this button, but don't open a now-stranded drawer if it was tapped
        // right before that happened.
        if (typeof specFiltersEl.nuSpecColdLoadFailed === 'function' && specFiltersEl.nuSpecColdLoadFailed()) return;
      }

      this.setupMobileFilterCards(true);
      this.filtersColumn.classList.add('is-open-mobile');
      document.documentElement.classList.add('nu-mobile-filters-open');
      document.body.classList.add('nu-mobile-filters-open');
      this.syncMobileFiltersAccessibility();
      // Move focus into the drawer once it's actually open (aria-hidden was just cleared
      // by syncMobileFiltersAccessibility() above — focusing before that would move focus
      // into a subtree still marked aria-hidden). The close button is the natural first
      // stop; fall back to whatever's first focusable if for some reason it's missing.
      const firstFocusable = this.mobileFiltersCloseBtn || this.getMobileFiltersFocusableElements()[0];
      firstFocusable?.focus();
    }

    closeMobileFilters() {
      if (!this.filtersColumn) return;
      const wasOpen = this.filtersColumn.classList.contains('is-open-mobile');
      this.filtersColumn.classList.remove('is-open-mobile');
      document.documentElement.classList.remove('nu-mobile-filters-open');
      document.body.classList.remove('nu-mobile-filters-open');
      this.syncMobileFiltersAccessibility();
      // Only steal focus back if it was actually inside the drawer — e.g. a resize past
      // the mobile breakpoint also calls this, and focus could be anywhere by then.
      if (wasOpen && this.filtersColumn.contains(document.activeElement)) {
        this.mobileFiltersOpenBtn?.focus();
      }
    }

    getMobileFiltersFocusableElements() {
      if (!this.filtersColumn) return [];
      return Array.from(this.filtersColumn.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((el) => el.offsetParent !== null);
    }

    trapMobileFiltersFocus(event) {
      const focusable = this.getMobileFiltersFocusableElements();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const isOutside = !this.filtersColumn.contains(document.activeElement);
      if (event.shiftKey) {
        if (isOutside || document.activeElement === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (isOutside || document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    handleWindowKeydown(event) {
      const isOpenMobile = Boolean(this.filtersColumn?.classList.contains('is-open-mobile'));
      if (event.key === 'Escape') {
        if (isOpenMobile) this.closeMobileFilters();
        return;
      }
      if (event.key === 'Tab' && isOpenMobile) {
        this.trapMobileFiltersFocus(event);
      }
    }

    setupMobileFilterCards(resetState = false) {
      if (!this.filtersColumn) return;
      const defaultState = this.filtersColumn.dataset.nuMobileCardsDefault || 'expanded';
      const collapseByDefault = defaultState === 'collapsed';
      const cardEls = this.filtersColumn.querySelectorAll('.nu-spec-filters__group, .nu--facet-fieldset');

      cardEls.forEach((cardEl, index) => {
        const legendEl = cardEl.querySelector('.nu-spec-filters__legend');
        const optionsEl = cardEl.querySelector('.nu-spec-filters__options');
        if (!legendEl || !optionsEl) return;

        const cardId = cardEl.id || `nu-mobile-filter-card-${index + 1}`;
        cardEl.id = cardId;
        if (!optionsEl.id) {
          optionsEl.id = `${cardId}-content`;
        }

        legendEl.classList.add('nu-mobile-filter-card__toggle');
        legendEl.setAttribute('role', 'button');
        legendEl.setAttribute('tabindex', '0');
        legendEl.setAttribute('aria-controls', optionsEl.id);

        const syncExpandedState = () => {
          const isCollapsed = cardEl.classList.contains('is-mobile-collapsed');
          legendEl.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
        };

        if (cardEl.dataset.nuMobileCardBound !== 'true') {
          const toggleCard = () => {
            if (!this.isMobileViewport()) return;
            cardEl.classList.toggle('is-mobile-collapsed');
            syncExpandedState();
          };

          legendEl.addEventListener('click', toggleCard);
          legendEl.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            toggleCard();
          });
          cardEl.dataset.nuMobileCardBound = 'true';
        }

        if (resetState || cardEl.dataset.nuMobileCardStateInitialized !== 'true') {
          cardEl.classList.toggle('is-mobile-collapsed', collapseByDefault);
          cardEl.dataset.nuMobileCardStateInitialized = 'true';
        }
        syncExpandedState();
      });
    }

    setupFloatingTableHead() {
      if (this.floatingHeadEl) {
        this.floatingHeadEl.remove();
        this.floatingHeadEl = null;
      }
      if (!this.layoutRoot || !this.tableElement || !this.tableHead) return;
      const sourceColGroup = this.tableElement.querySelector('colgroup');
      if (!sourceColGroup) return;

      const floatingEl = document.createElement('div');
      floatingEl.className = 'nu-products-table__floating-head';
      floatingEl.hidden = true;
      floatingEl.setAttribute('aria-hidden', 'true');

      const floatingTable = document.createElement('table');
      floatingTable.className = this.tableElement.className || 'nu-products-table__table';
      floatingTable.innerHTML = `${sourceColGroup.outerHTML}${this.tableHead.outerHTML}`;
      floatingEl.appendChild(floatingTable);
      this.layoutRoot.appendChild(floatingEl);
      this.floatingHeadEl = floatingEl;
      this.floatingTableEl = floatingTable;
      this.floatingHeadCells = Array.from(
        floatingTable.querySelectorAll('.nu-products-table__head .nu-products-table__cell')
      );
    }

    handleFloatingHeadPosition(immediate = false) {
      if (immediate) {
        if (this.floatingHeadRafId) {
          window.cancelAnimationFrame(this.floatingHeadRafId);
          this.floatingHeadRafId = null;
        }
        this.floatingHeadRafPending = false;
        this.syncFloatingHeadPosition();
        this.syncVirtualScrollbarPosition();
        return;
      }
      if (this.floatingHeadRafPending) return;
      this.floatingHeadRafPending = true;
      this.floatingHeadRafId = window.requestAnimationFrame(() => {
        this.floatingHeadRafId = null;
        this.floatingHeadRafPending = false;
        this.syncFloatingHeadPosition();
        this.syncVirtualScrollbarPosition();
      });
    }

    syncFloatingHeadPosition() {
      if (!this.tableScroller || !this.tableElement || !this.tableHead || !this.floatingHeadEl) return;
      if (!this.isMobileViewport()) {
        this.closeMobileFilters();
      }
      this.syncMobileFiltersAccessibility();
      // Measure the sticky header's current position directly rather than reading
      // --header-end/--header-end-padded: those are only kept fresh on scroll by
      // main.js on desktop viewports, so on mobile they go stale after the
      // announcement bar scrolls away, leaving a gap (or hiding the floating head
      // entirely if it skews the shouldFloat comparison below).
      const headerOffset = this.stickyHeaderEl
        ? Math.max(0, this.stickyHeaderEl.getBoundingClientRect().bottom)
        : 0;
      const scrollerRect = this.tableScroller.getBoundingClientRect();
      const tableRect = this.tableElement.getBoundingClientRect();
      const headHeight = this.tableHead.getBoundingClientRect().height;
      const shouldFloat = scrollerRect.top <= headerOffset && scrollerRect.bottom > headerOffset + headHeight;

      this.floatingHeadEl.hidden = !shouldFloat;
      this.tableHead.classList.toggle('is-floating-active', shouldFloat);

      if (!shouldFloat) return;
      this.syncFloatingHeadCellWidths();
      this.floatingHeadEl.style.top = `${headerOffset}px`;
      this.floatingHeadEl.style.left = `${scrollerRect.left}px`;
      this.floatingHeadEl.style.width = `${scrollerRect.width}px`;
      if (this.floatingTableEl) {
        this.floatingTableEl.style.width = `${tableRect.width}px`;
      }
      this.floatingHeadEl.scrollLeft = this.tableScroller.scrollLeft;
    }

    syncFloatingHeadCellWidths() {
      if (!this.floatingHeadCells || this.floatingHeadCells.length === 0 || !this.tableHead) return;
      const sourceHeadCells = Array.from(this.tableHead.querySelectorAll('.nu-products-table__cell'));
      if (sourceHeadCells.length !== this.floatingHeadCells.length) return;

      sourceHeadCells.forEach((sourceCell, idx) => {
        const width = sourceCell.getBoundingClientRect().width;
        const floatingCell = this.floatingHeadCells[idx];
        floatingCell.style.width = `${width}px`;
        floatingCell.style.minWidth = `${width}px`;
        floatingCell.style.maxWidth = `${width}px`;
      });
    }

    setupTableDragScroll() {
      if (!this.tableScroller) return;
      this.tableScroller.removeEventListener('pointerdown', this.onTableScrollerPointerDown);
      this.tableScroller.addEventListener('pointerdown', this.onTableScrollerPointerDown);
    }

    handleTableScrollerPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest('a, button, input, select, textarea, label, [contenteditable="true"]')) return;

      const state = this.tableDragScroll;
      state.active = true;
      state.moved = false;
      state.startX = event.clientX;
      state.startScrollLeft = this.tableScroller.scrollLeft;
      window.addEventListener('pointermove', this.onTableScrollerPointerMove);
      window.addEventListener('pointerup', this.onTableScrollerPointerUp);
      window.addEventListener('pointercancel', this.onTableScrollerPointerUp);
    }

    cancelTableDragScroll() {
      const state = this.tableDragScroll;
      if (!state.active) return;
      state.active = false;
      state.moved = false;
      if (this.tableScroller) {
        this.tableScroller.classList.remove('is-drag-scrolling');
      }
      window.removeEventListener('pointermove', this.onTableScrollerPointerMove);
      window.removeEventListener('pointerup', this.onTableScrollerPointerUp);
      window.removeEventListener('pointercancel', this.onTableScrollerPointerUp);
    }

    handleTableScrollerPointerMove(event) {
      const state = this.tableDragScroll;
      if (!state.active) return;
      const deltaX = event.clientX - state.startX;
      if (!state.moved && Math.abs(deltaX) > 3) {
        state.moved = true;
        this.tableScroller.classList.add('is-drag-scrolling');
      }
      if (state.moved) {
        event.preventDefault();
        this.tableScroller.scrollLeft = state.startScrollLeft - deltaX;
      }
    }

    handleTableScrollerPointerUp(event) {
      const state = this.tableDragScroll;
      if (!state.active) return;
      state.active = false;
      this.tableScroller.classList.remove('is-drag-scrolling');
      window.removeEventListener('pointermove', this.onTableScrollerPointerMove);
      window.removeEventListener('pointerup', this.onTableScrollerPointerUp);
      window.removeEventListener('pointercancel', this.onTableScrollerPointerUp);
      // pointercancel has no matching click to suppress - only arm the swallower for a
      // real pointerup, and drop it on the next tick in case the click never arrives
      // (e.g. the pointer left the document), so it can't catch an unrelated later click.
      if (state.moved && event?.type === 'pointerup') {
        window.addEventListener('click', ProductsToolbar.suppressNextClick, { capture: true, once: true });
        window.setTimeout(() => {
          window.removeEventListener('click', ProductsToolbar.suppressNextClick, { capture: true });
        }, 0);
      }
    }

    static suppressNextClick(event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setupTableResizeObserver() {
      if (this.tableResizeObserver) {
        this.tableResizeObserver.disconnect();
        this.tableResizeObserver = null;
      }
      if (!this.tableScroller || !window.ResizeObserver) return;
      this.tableResizeObserver = new ResizeObserver(() => {
        this.handleTableScroll();
      });
      this.tableResizeObserver.observe(this.tableScroller);
    }

    setupVirtualScrollbar() {
      if (this.virtualScrollbarEl) {
        if (this.onVirtualScrollbarScroll) {
          this.virtualScrollbarEl.removeEventListener('scroll', this.onVirtualScrollbarScroll);
        }
        this.virtualScrollbarEl.remove();
        this.virtualScrollbarEl = null;
        this.virtualScrollbarInnerEl = null;
      }
      if (!this.tableScroller) return;

      const wrapEl = document.createElement('div');
      wrapEl.className = 'nu-products-table__virtual-scrollbar';
      wrapEl.hidden = true;
      wrapEl.setAttribute('aria-hidden', 'true');

      const innerEl = document.createElement('div');
      innerEl.className = 'nu-products-table__virtual-scrollbar-inner';
      wrapEl.appendChild(innerEl);

      document.body.appendChild(wrapEl);
      this.virtualScrollbarEl = wrapEl;
      this.virtualScrollbarInnerEl = innerEl;
      this.virtualScrollbarEl.addEventListener('scroll', this.onVirtualScrollbarScroll, { passive: true });
    }

    handleVirtualScrollbarScroll() {
      if (!this.tableScroller || !this.virtualScrollbarEl || this.isSyncingVirtualScrollbar) return;
      this.isSyncingVirtualScrollbar = true;
      this.tableScroller.scrollLeft = this.virtualScrollbarEl.scrollLeft;
      this.isSyncingVirtualScrollbar = false;
    }

    syncVirtualScrollbarPosition() {
      if (!this.tableScroller || !this.virtualScrollbarEl) return;
      const maxScrollLeft = Math.max(this.tableScroller.scrollWidth - this.tableScroller.clientWidth, 0);
      if (maxScrollLeft <= 0) {
        this.virtualScrollbarEl.hidden = true;
        return;
      }

      const scrollerRect = this.tableScroller.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      // Only stand in while part of the table is on screen but its own bottom edge
      // (where the native horizontal scrollbar lives) has scrolled past the viewport.
      const shouldShow = scrollerRect.top < viewportHeight && scrollerRect.bottom > viewportHeight;

      this.virtualScrollbarEl.hidden = !shouldShow;
      if (!shouldShow) return;

      this.virtualScrollbarEl.style.left = `${scrollerRect.left}px`;
      this.virtualScrollbarEl.style.width = `${scrollerRect.width}px`;
      if (this.virtualScrollbarInnerEl) {
        this.virtualScrollbarInnerEl.style.width = `${this.tableScroller.scrollWidth}px`;
      }
      if (!this.isSyncingVirtualScrollbar) {
        this.isSyncingVirtualScrollbar = true;
        this.virtualScrollbarEl.scrollLeft = this.tableScroller.scrollLeft;
        this.isSyncingVirtualScrollbar = false;
      }
    }

    static extractRenderedProducts(rootEl = document) {
      const output = {
        orderedHandles: [],
        handleToHtml: {},
        handleToSkus: {},
      };
      if (!rootEl) return output;
      rootEl.querySelectorAll('.main-products-grid__results [data-product-handle]').forEach((item) => {
        const handle = item.dataset.productHandle;
        if (!handle || output.handleToHtml[handle]) return;
        output.orderedHandles.push(handle);
        output.handleToHtml[handle] = item.outerHTML;
        output.handleToSkus[handle] = ProductsToolbar.getProductSkus(item);
      });
      return output;
    }

    static mergeRenderedProducts(baseData, incomingData) {
      const merged = {
        orderedHandles: [...(baseData?.orderedHandles || [])],
        handleToHtml: { ...(baseData?.handleToHtml || {}) },
        handleToSkus: { ...(baseData?.handleToSkus || {}) },
      };

      (incomingData?.orderedHandles || []).forEach((handle) => {
        if (!merged.handleToHtml[handle] && incomingData.handleToHtml?.[handle]) {
          merged.orderedHandles.push(handle);
          merged.handleToHtml[handle] = incomingData.handleToHtml[handle];
        }
        if (!merged.handleToSkus[handle] && incomingData.handleToSkus?.[handle]) {
          merged.handleToSkus[handle] = incomingData.handleToSkus[handle];
        }
      });

      return merged;
    }

    captureSkuServerSnapshot(rowsContainer, { abortGlobalFetch = true } = {}) {
      if (this.hasNuSpecFilters()) return;
      const tbody = rowsContainer || document.querySelector('.main-products-grid__results .nu-products-table__body');
      const paginationWrap = document.querySelector('.main-products-grid__results nav[aria-label]');
      this.skuServerSnapshot.page = this.getCurrentPageFromUrl();
      if (tbody) {
        this.skuServerSnapshot.rowsHtml = tbody.innerHTML;
      }
      if (paginationWrap) {
        this.skuServerSnapshot.paginationHtml = paginationWrap.innerHTML;
      }
      if (!abortGlobalFetch) return;
      // Dropping the promise reference here doesn't stop the fetch it points to — without
      // aborting it, an in-flight getGlobalRenderedProductsForSku() call keeps running in
      // the background and can still overwrite skuGlobalCache with stale data once it
      // resolves, after this snapshot has already moved on. Only call sites that represent an
      // actual state transition (SKU search cleared, page changed) should pass this — see
      // handleR4QRefreshDone(), which explicitly opts out because R4Q settling isn't one.
      if (this.skuGlobalFetchController) {
        this.skuGlobalFetchController.abort();
        this.skuGlobalFetchController = null;
      }
      this.skuGlobalCache = null;
      this.skuGlobalFetchPromise = null;
    }

    restoreSkuServerSnapshot() {
      if (this.getCurrentPageFromUrl() !== this.skuServerSnapshot.page) {
        window.location.href = window.location.toString();
        return;
      }
      const tbody = document.querySelector('.main-products-grid__results .nu-products-table__body');
      const paginationWrap = document.querySelector('.main-products-grid__results nav[aria-label]');
      if (tbody && this.skuServerSnapshot.rowsHtml) {
        tbody.innerHTML = this.skuServerSnapshot.rowsHtml;
      }
      if (paginationWrap && this.skuServerSnapshot.paginationHtml) {
        paginationWrap.innerHTML = this.skuServerSnapshot.paginationHtml;
        paginationWrap.hidden = false;
      }
    }

    getRowsPerPageFromUrl() {
      const fromUrl = this.filtersLite?.getPositiveIntParam
        ? this.filtersLite.getPositiveIntParam('nu_rows', 0)
        : 0;
      if (fromUrl > 0) return fromUrl;
      const fromSelect = Number(this.rowsSelect?.value || '');
      if (Number.isFinite(fromSelect) && fromSelect > 0) return fromSelect;
      const currentRows = document.querySelectorAll('.main-products-grid__results [data-product-handle]').length;
      return currentRows > 0 ? currentRows : 25;
    }

    getCurrentPageFromUrl() {
      if (this.filtersLite?.getPositiveIntParam) {
        return this.filtersLite.getPositiveIntParam('page', 1);
      }
      const params = new URLSearchParams(window.location.search);
      const parsed = Number(params.get('page') || '1');
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
    }

    getTotalPagesFromPagination() {
      const paginationWrap = document.querySelector('.main-products-grid__results nav[aria-label]');
      if (!paginationWrap) return 1;
      // nu--pagination.liquid's desktop page-number list only ever renders a fixed window of
      // page numbers (current page ±2), so scanning visible link/span text (the fallback below)
      // silently undercounts the real total once a collection has more than ~5 pages — which
      // in turn makes getGlobalRenderedProductsForSku() stop fetching pages way before the end
      // of the collection (H-32). Prefer the real paginate.pages value rendered onto the nav.
      const fromAttr = Number(paginationWrap.getAttribute('data-total-pages') || '');
      if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;
      let maxPage = 1;
      paginationWrap.querySelectorAll('a[href], span').forEach((el) => {
        const text = String(el.textContent || '').trim();
        if (!/^\d+$/.test(text)) return;
        const num = Number(text);
        if (Number.isFinite(num) && num > maxPage) maxPage = num;
      });
      return maxPage;
    }

    getSectionRenderId() {
      const sectionEl = this.closest('.shopify-section');
      const sectionDomId = sectionEl?.id || '';
      if (!sectionDomId.startsWith('shopify-section-')) return '';
      return sectionDomId.replace('shopify-section-', '');
    }

    parseRenderedProductsFromResponse(payloadText) {
      if (!payloadText || typeof payloadText !== 'string') {
        return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
      }
      const parser = new DOMParser();
      const doc = parser.parseFromString(payloadText, 'text/html');
      const fromHtml = ProductsToolbar.extractRenderedProducts(doc);
      if (fromHtml.orderedHandles.length > 0) return fromHtml;

      try {
        const jsonPayload = JSON.parse(payloadText);
        if (jsonPayload && typeof jsonPayload === 'object') {
          const firstHtml = Object.values(jsonPayload).find((value) => typeof value === 'string');
          if (firstHtml) {
            const parsedDoc = parser.parseFromString(firstHtml, 'text/html');
            return ProductsToolbar.extractRenderedProducts(parsedDoc);
          }
        }
      } catch (error) {
        return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
      }

      return { orderedHandles: [], handleToHtml: {}, handleToSkus: {} };
    }

    async getGlobalRenderedProductsForSku(options = {}) {
      const onProgress = typeof options.onProgress === 'function' ? options.onProgress : null;
      if (this.skuGlobalCache) return this.skuGlobalCache;
      if (this.skuGlobalFetchPromise) return this.skuGlobalFetchPromise;

      // The page number the snapshot below belongs to — NOT the live URL's: a rows-per-page
      // change drops ?page= (-> 1) without reloading, but the snapshot is still whatever page
      // the server rendered on load (e.g. an out-of-range, empty page 2, H-41).
      const currentPage = this.ssrPage;
      // The next two come from the SSR snapshot, NOT the live DOM/pagination: changing rows-per-page
      // replaces the table rows with a truncated slice and builds client-side pagination, so
      // after 25 -> 10 the DOM holds only 10 rows and the nav claims 2 pages. Treating that as
      // "the current page's data" cached a 10-row list (and fetched pages the server renders
      // empty), so switching back to 25 showed 10 rows with no pagination, and SKU search
      // could not find the other products (H-42).
      const totalPages = this.ssrTotalPages;
      const currentData = this.ssrRenderedProducts;
      // A one-page collection's only page IS the whole product list, so the DOM already holds
      // it — but only while the URL is actually on page 1. Under ?nu_rows=10&page=2 (what the
      // pagination click in renderRowsClientPagination() navigates to, H-33) the server still
      // paginates by the default page size, so page 2 of a collection that fits in one default
      // page is out of range and rendered EMPTY. The DOM then holds nothing, and page 1 has to
      // be fetched like any other page — returning the empty DOM here left the table blank (H-41).
      if (totalPages <= 1 && currentPage <= totalPages) {
        this.skuGlobalCache = currentData;
        return this.skuGlobalCache;
      }

      const sectionRenderId = this.getSectionRenderId();
      const currentUrl = new URL(window.location.href);
      const pendingPages = [];
      for (let page = 1; page <= totalPages; page += 1) {
        if (page !== currentPage) pendingPages.push(page);
      }
      pendingPages.sort((a, b) => (
        Math.abs(a - currentPage) - Math.abs(b - currentPage)
      ));

      // A prior call's fetches are still using the connection pool below for a page range
      // that no longer matters once a newer call starts (e.g. the user paginated again).
      if (this.skuGlobalFetchController) this.skuGlobalFetchController.abort();
      const controller = new AbortController();
      this.skuGlobalFetchController = controller;

      this.skuGlobalFetchPromise = (async () => {
        // Fetch every other page concurrently (capped, see SKU_FETCH_CONCURRENCY) instead
        // of one at a time, but keep the merge order stable (proximity to current page)
        // regardless of which fetch actually resolves first, so results stay deterministic
        // across reloads.
        // Keyed by page number (not fetch/resolve order) so the merge below always walks
        // pages in ascending order regardless of which one resolves first or the distance-
        // based fetch priority above.
        const resultsByPage = new Map();
        const pagesAscending = [...pendingPages].sort((a, b) => a - b);
        let resolvedCount = 0;

        const computeMerged = () => {
          let merged = currentData;
          for (const page of pagesAscending) {
            const pageData = resultsByPage.get(page);
            if (pageData) merged = ProductsToolbar.mergeRenderedProducts(merged, pageData);
          }
          return merged;
        };

        if (onProgress) onProgress(currentData, { isFinal: false });

        const fetchOnePage = async (page) => {
          const requestUrl = new URL(currentUrl.toString());
          requestUrl.searchParams.set('page', String(page));
          requestUrl.searchParams.set('_nu_sku_fetch', String(page));
          if (sectionRenderId) {
            requestUrl.searchParams.set('section_id', sectionRenderId);
          }

          const pageData = await fetch(requestUrl.toString(), { credentials: 'same-origin', signal: controller.signal })
            .then((response) => (response.ok ? response.text() : ''))
            .then((responseText) => this.parseRenderedProductsFromResponse(responseText))
            .catch(() => ({ orderedHandles: [], handleToHtml: {}, handleToSkus: {} }));
          resultsByPage.set(page, pageData);
          resolvedCount += 1;
          if (onProgress) {
            onProgress(computeMerged(), { isFinal: resolvedCount === pendingPages.length, page });
          }
        };

        if (this.filtersLite?.runWithConcurrency) {
          await this.filtersLite.runWithConcurrency(pendingPages, SKU_FETCH_CONCURRENCY, fetchOnePage);
        } else {
          await Promise.all(pendingPages.map((page) => fetchOnePage(page)));
        }

        const merged = computeMerged();
        // A newer call may have aborted this controller and started its own fetch — caching
        // this (possibly abort-truncated) result now would let it win the race and serve
        // stale/incomplete data to every future getGlobalRenderedProductsForSku() call.
        if (!controller.signal.aborted && this.skuGlobalFetchController === controller) {
          this.skuGlobalCache = merged;
        }
        return merged;
      })().finally(() => {
        this.skuGlobalFetchPromise = null;
        if (this.skuGlobalFetchController === controller) this.skuGlobalFetchController = null;
      });

      return this.skuGlobalFetchPromise;
    }

    renderSkuClientPagination(totalItems, currentPageNum, rowsPerPage) {
      const paginationWrap = document.querySelector('.main-products-grid__results nav[aria-label]');
      if (!paginationWrap) return;
      this.renderClientPagination(paginationWrap, totalItems, currentPageNum, rowsPerPage, 'data-nu-sku-page');
      if (paginationWrap.dataset.nuSkuPaginationBound === 'true') return;
      paginationWrap.addEventListener('click', (event) => {
        const link = event.target.closest('[data-nu-sku-page]');
        if (!link) return;
        event.preventDefault();
        const targetPage = Number(link.getAttribute('data-nu-sku-page'));
        if (!Number.isFinite(targetPage) || targetPage < 1) return;
        const url = new URL(window.location.href);
        if (targetPage <= 1) {
          url.searchParams.delete('page');
        } else {
          url.searchParams.set('page', String(targetPage));
        }
        // A real navigation instead of client truncation (H-33 — see the identical rationale
        // in renderRowsClientPagination()). nu_sku is already persisted in the URL, and cold
        // load re-populates the search input and re-applies the filter from it, so this loses
        // nothing while avoiding the same "DOM already narrowed by a prior client rebuild"
        // class of bug.
        window.location.href = url.toString();
      });
      paginationWrap.dataset.nuSkuPaginationBound = 'true';
    }

    shouldUseGlobalSkuSearch() {
      if (this.hasNuSpecFilters()) return false;
      const hasTableBody = Boolean(document.querySelector('.main-products-grid__results .nu-products-table__body'));
      return hasTableBody;
    }

    async applyGlobalSkuSearchFilter(query, options = {}) {
      const busyOverlay = Boolean(options.busyOverlay);
      const token = this.skuSearchRequestToken + 1;
      this.skuSearchRequestToken = token;
      const tbody = document.querySelector('.main-products-grid__results .nu-products-table__body');
      if (!tbody || !this.productsTotal) return;
      let lastProgressRefreshAt = 0;

      if (!query) {
        this.setSkuSearchStatusVisible(false);
        this.restoreSkuServerSnapshot();
        this.setProductsTotalText(this.baseResultsCount);
        this.requestR4QRefresh({ busy: busyOverlay });
        return;
      }

      // Fast path: render from current DOM first, then progressively refine once global pages are ready.
      const hasGlobalCache = Boolean(this.skuGlobalCache);
      if (!hasGlobalCache) {
        const currentOnly = ProductsToolbar.extractRenderedProducts(document);
        this.renderGlobalSkuMatches(currentOnly, query);
        // The count/rows just rendered only reflect the current page, not the full catalog —
        // show a "Searching..." status until the cross-page fetch below resolves and replaces
        // it, so a match that isn't on this page doesn't read as "search finished, found
        // nothing" (H-32 in .doc/full-project-review).
        this.setSkuSearchStatusVisible(true);
      }

      const rendered = await this.getGlobalRenderedProductsForSku({
        onProgress: (partialRendered, progressMeta = {}) => {
          if (token !== this.skuSearchRequestToken) return;
          const result = this.renderGlobalSkuMatches(partialRendered, query);
          if (!result.didReplaceDom) return;
          const now = Date.now();
          if (progressMeta.isFinal || now - lastProgressRefreshAt > 800) {
            lastProgressRefreshAt = now;
            this.requestR4QRefresh({ busy: false });
          }
        },
      });
      if (token !== this.skuSearchRequestToken) return;

      this.setSkuSearchStatusVisible(false);
      const renderResult = this.renderGlobalSkuMatches(rendered, query);
      if (renderResult.didReplaceDom) {
        this.requestR4QRefresh({ busy: busyOverlay });
      }
    }

    setSkuSearchStatusVisible(isVisible) {
      if (this.skuSearchStatusEl) this.skuSearchStatusEl.hidden = !isVisible;
    }

    // Borrows the same overlay element as setSkuSearchStatusVisible() (H-32) for the cold-load
    // nu_rows correction (H-34/H-37) instead of a second overlay — the two triggers never
    // overlap in practice (this only ever runs once, synchronously at init, before any SKU
    // search input exists), but swap in data-nu-loading-text's wording while visible and
    // restore the original "Searching…" text afterwards so a later SKU search still reads
    // correctly.
    setRowsLoadingStatusVisible(isVisible) {
      if (!this.skuSearchStatusEl) return;
      const text = isVisible
        ? (this.skuSearchStatusEl.dataset.nuLoadingText || this.skuSearchStatusDefaultText)
        : this.skuSearchStatusDefaultText;
      if (text) this.skuSearchStatusEl.textContent = text;
      this.skuSearchStatusEl.hidden = !isVisible;
    }

    renderGlobalSkuMatches(rendered, query) {
      const rowsPerPage = this.getRowsPerPageFromUrl();
      const tbody = document.querySelector('.main-products-grid__results .nu-products-table__body');
      if (!tbody || !this.productsTotal) return { didReplaceDom: false };

      const matchedHandles = rendered.orderedHandles.filter((handle) => {
        const skus = rendered.handleToSkus?.[handle] || [];
        return skus.some((sku) => sku.includes(query));
      });

      const totalMatched = matchedHandles.length;
      const totalPages = Math.max(1, Math.ceil(totalMatched / rowsPerPage));
      const currentPage = this.getCurrentPageFromUrl();
      const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
      if (safeCurrentPage !== currentPage) {
        const normalizedUrl = new URL(window.location.href);
        if (safeCurrentPage <= 1) normalizedUrl.searchParams.delete('page');
        else normalizedUrl.searchParams.set('page', String(safeCurrentPage));
        history.replaceState({}, '', normalizedUrl.toString());
      }

      const startIndex = (safeCurrentPage - 1) * rowsPerPage;
      const pageHandles = matchedHandles.slice(startIndex, startIndex + rowsPerPage);
      const currentRows = Array.from(tbody.querySelectorAll('tr[data-product-handle]'));
      const currentRowMap = new Map();
      currentRows.forEach((rowEl) => {
        const handle = String(rowEl.getAttribute('data-product-handle') || '');
        if (!handle || currentRowMap.has(handle)) return;
        currentRowMap.set(handle, rowEl);
      });

      const canReuseCurrentRows = pageHandles.every((handle) => currentRowMap.has(handle));
      let didReplaceDom = false;
      if (canReuseCurrentRows) {
        const pageHandleSet = new Set(pageHandles);
        currentRows.forEach((rowEl) => {
          const handle = String(rowEl.getAttribute('data-product-handle') || '');
          rowEl.hidden = !pageHandleSet.has(handle);
        });
        const fragment = document.createDocumentFragment();
        pageHandles.forEach((handle) => {
          const rowEl = currentRowMap.get(handle);
          if (rowEl) {
            rowEl.hidden = false;
            fragment.appendChild(rowEl);
          }
        });
        if (fragment.childNodes.length > 0) {
          tbody.appendChild(fragment);
        }
      } else {
        tbody.innerHTML = pageHandles.map((handle) => rendered.handleToHtml[handle]).filter(Boolean).join('');
        didReplaceDom = true;
      }

      this.setProductsTotalText(totalMatched);
      this.renderSkuClientPagination(totalMatched, safeCurrentPage, rowsPerPage);
      return { didReplaceDom };
    }

    applySkuSearchFilter(options = {}) {
      const busyOverlay = Boolean(options.busyOverlay);
      const query = this.getSkuSearchQuery();
      if (this.shouldUseGlobalSkuSearch()) {
        this.applyGlobalSkuSearchFilter(query, { busyOverlay });
        return;
      }

      const cards = Array.from(document.querySelectorAll('.main-products-grid__results [data-product-handle]'));
      if (!this.productsTotal || cards.length === 0) return;

      let visibleCount = 0;

      cards.forEach((card) => {
        const visibleByBaseFilters = ProductsToolbar.isProductVisibleByOtherFilters(card);
        if (!visibleByBaseFilters) {
          card.classList.remove('nu-sku-hidden');
          return;
        }

        if (!query) {
          card.classList.remove('nu-sku-hidden');
          visibleCount += 1;
          return;
        }

        const productSkus = ProductsToolbar.getProductSkus(card);
        const isMatched = productSkus.some((sku) => sku.includes(query));
        card.classList.toggle('nu-sku-hidden', !isMatched);
        if (isMatched) visibleCount += 1;
      });

      const nextCount = query ? visibleCount : this.baseResultsCount;
      this.setProductsTotalText(nextCount);
      this.requestR4QRefresh({ busy: busyOverlay });
    }

    /**
     * Handles 'change' events on the layout switcher buttons.
     * @param {object} evt - Event object.
     * @param {string} layout - 'grid' or 'list' - the layout to change to.
     */
    handleLayoutChange(evt, layout) {
      if (!this.productsGrid) return;
      this.productsGrid.dataset.layout = layout || evt.target.value;
      if (!layout) theme.storageUtil.set('products-layout', evt.target.value);
    }

    /**
     * Handles when a sort by dropdown is changed (and filtering is disabled)
     * @param {object} evt - Event object.
     */
    static handleSortByChange(evt) {
      const urlObj = new URL(window.location.href);
      urlObj.searchParams.set('sort_by', evt.detail.selectedValue);
      urlObj.hash = 'products-toolbar';
      window.location.href = urlObj.toString();
    }
  }

  customElements.define('products-toolbar', ProductsToolbar);
}
